import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import DeleteConfirmationModal from "../../../components/DeleteModal";
import Search from "../../../components/Search";
import AddCustomerForm from "../AddCustomerForm";
import TableSkeleton from "../../../components/Skeleton/TableSkeleton";
import { Empty, message } from "antd";
import { getVisiblePages } from "../../../utils/pagination";
import Delete from "../../../assets/images/delete.png";
import ManageLink from "../../../assets/images/manage-link.png";
import {
  fetchCustomers,
  CustomerRecord,
  deactivateCustomer,
} from "../services/customerService";
import { useNavigate, useLocation } from "react-router-dom";
import { setSelectedCustomerId } from "../../msp/slices/selectedCustomerSlice";
import { setSelectedMspId } from "../../msp/slices/selectedMspSlice";
import { formatDate } from "../../../utils/formatDate";
import { useDebounce } from "use-debounce";
import { getCognitoLoginUrl } from "../../../config/cognito";
import { useMSPData } from "../../../hooks/useMSPData";

interface CustomerTableProps {
  mspId?: string | null;
  onBack?: () => void;
}

export default function CustomerTable({ mspId }: Readonly<CustomerTableProps>) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMspPortal = location.pathname.startsWith("/customers");
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRows: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] =
    useState<CustomerRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [refresh, setRefresh] = useState(0);
  const effectiveMspId = Number(mspId);
  const hasValidMspId = Number.isFinite(effectiveMspId) && effectiveMspId > 0;

  const { msp: mspData } = useMSPData(hasValidMspId ? effectiveMspId : 0);

  const handleMspNameClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasValidMspId && mspData) {
      dispatch(setSelectedMspId(effectiveMspId));
      if (isMspPortal) {
        navigate("/msp/info");
      } else {
        navigate(`/msp/${effectiveMspId}`);
      }
    }
  };

  const loadCustomers = async () => {
    if (!hasValidMspId) {
      console.error("Invalid MSP ID:", effectiveMspId);
      setError("No Customers Found");
      setCustomers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetchCustomers({
        mspId: effectiveMspId,
        page: pagination.currentPage,
        limit: 10,
        search: debouncedSearchTerm,
      });
      const activeCustomers = response.data.filter(
        (customer: CustomerRecord) => customer.status !== 0
      );

      setCustomers(activeCustomers);
      setPagination(response.pagination);
    } catch (e: any) {
      console.error("Error loading customers:", e);
      setError(e.message || "Failed to load customers");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const state = location.state;
    if (state?.resetMspView) {
      setShowAddForm(false);
      setSearchTerm("");
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    }
  }, [location.state]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearchTerm]);

  useEffect(() => {
    loadCustomers();
  }, [
    pagination.currentPage,
    debouncedSearchTerm,
    refresh,
    hasValidMspId,
    effectiveMspId,
  ]);

  const handleRefresh = () => {
    setRefresh((prev) => prev + 1);
  };

  const handleDeleteClick = (customer: CustomerRecord) => {
    setCustomerToDelete(customer);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!customerToDelete) return;
    setDeleting(true);
    try {
      if (!hasValidMspId) return;

      await deactivateCustomer(effectiveMspId, customerToDelete.id);

      setShowDeleteModal(false);
      setCustomerToDelete(null);

      await loadCustomers();
    } catch (e: any) {
      message.error("Delete failed. Please try again.");
      console.error("Delete error:", e);
    } finally {
      setDeleting(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleCustomerClick = (customerId: number) => {
    dispatch(setSelectedCustomerId(customerId));
    navigate(`/msp/${mspId}/customer/${customerId}`);
  };

  if (showAddForm) {
    return (
      <AddCustomerForm
        onCancel={() => setShowAddForm(false)}
        onSuccess={handleRefresh}
        mspId={mspId ?? undefined}
      />
    );
  }

  return (
    <div className="px-2 pb-8">
      {showDeleteModal && (
        <DeleteConfirmationModal
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setCustomerToDelete(null);
          }}
          loading={deleting}
          entityType="customer"
          entityName={customerToDelete?.organization_name}
        />
      )}

      <div className="pt-2 flex items-start justify-between">
        <div>
          <h2 className="text-[20px] font-semibold text-[#4F4F4F]">
            MSP:{" "}
            {mspData ? (
              isMspPortal ? (
                <button
                  type="button"
                  className="text-[#28418F] cursor-pointer font-bold text-[22px]"
                  onClick={handleMspNameClick}
                  title="View MSP Information"
                >
                  {mspData.msp_name}
                </button>
              ) : (
                <span className="text-[#000000] font-bold text-[22px]">
                  {mspData.msp_name}
                </span>
              )
            ) : (
              "-"
            )}
          </h2>

          <h3 className="text-[22px] font-semibold text-[#4F4F4F] mt-1">
            Customer List
          </h3>

          <p className="text-[#4F4F4F] text-[14px] mt-1">
            Centralized insights for streamlined management.
          </p>
        </div>

        <div className="min-w-[260px]">
          <Search
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            placeholder="Search by keywords"
          />
        </div>
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center gap-2 bg-[#1D58E2] text-white text-[16px] font-semibold px-4 py-2 shadow hover:brightness-110 rounded-sm hover:cursor-pointer"
        >
          <span className="text-[18px] leading-none">+</span> ADD NEW CUSTOMERS
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-md border border-[#EFEFEF]">
        <div className="grid grid-cols-[1.2fr_0.7fr_0.8fr_0.7fr] bg-[#F3F3F3] px-6 py-3 text-[12px] font-bold tracking-wide text-[#383838]">
          <div>NAME AND EMAIL ADDRESS</div>
          <div>CREATED ON</div>
          <div>CONTACT NUMBER</div>
          <div className="flex justify-start pr-1">ACTION</div>
        </div>

        {loading ? (
          <TableSkeleton rowCount={5} />
        ) : customers.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <Empty description={error || "No customers found"} />
          </div>
        ) : (
          <>
            <ul className="divide-y divide-[#EFEFEF]">
              {customers.map((customer) => (
                <li
                  key={customer.id}
                  className="grid grid-cols-[1.2fr_0.7fr_0.8fr_0.7fr] px-6 py-4 hover:bg-[#FAFAFA]"
                >
                  {/* col: name + email */}
                  <div className="pr-3">
                    <div className="flex items-start">
                      <button
                        type="button"
                        className="font-semibold text-[#161616] hover:text-[#1F5EFF] text-left hover:cursor-pointer"
                        onClick={() => handleCustomerClick(customer.id)}
                      >
                        {customer.organization_name}
                      </button>
                    </div>
                    <span className="text-[#25567C] text-[13px] ml-0 cursor-default select-text">
                      {customer.work_email_address}
                    </span>
                  </div>

                  {/* col: created on */}
                  <div className="text-[14px] text-[#3D3D3D] flex items-center">
                    {customer.createdAt
                      ? formatDate(customer.createdAt)
                      : "N/A"}
                  </div>

                  {/* col: phone */}
                  <div className="text-[14px] text-[#161616] flex items-center">
                    {customer.phone_code} {customer.mobile_phone}
                  </div>

                  {/* col: actions */}
                  <div className="flex items-center justify-start gap-5 whitespace-nowrap flex-nowrap">
                    <button
                      type="button"
                      title="Manage Link"
                      className="inline-flex items-center gap-2 text-[#1F5EFF] hover:underline whitespace-nowrap hover:cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          getCognitoLoginUrl(
                            customer.schema?.schema_name || ""
                          ),
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                    >
                      <img
                        src={ManageLink}
                        alt=""
                        aria-hidden="true"
                        className="w-3 h-3"
                      />
                      <span className="text-[13px] hover:cursor-pointer">
                        Manage Link
                      </span>
                    </button>
                    <button
                      type="button"
                      title="Delete Customer"
                      className="inline-flex items-center gap-2 text-[#E54848] hover:underline whitespace-nowrap hover:cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(customer);
                      }}
                    >
                      <img src={Delete} alt="Delete" className="w-4 h-4" />
                      <span className="text-[13px]">Delete</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-end pt-6 pb-4 px-2">
                <nav>
                  <ul className="flex space-x-1 rounded-2xl px-2 py-1 bg-[#f5f5f6] text-sm font-medium items-center">
                    <li>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded-full ${
                          pagination.currentPage === 1
                            ? "text-gray-400 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={pagination.currentPage === 1}
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: prev.currentPage - 1,
                          }))
                        }
                      >
                        Prev
                      </button>
                    </li>
                    {getVisiblePages(
                      pagination.currentPage,
                      pagination.totalPages
                    ).map((pg, idx) =>
                      pg === "..." ? (
                        <li
                          key={pg + idx}
                          className="px-2 py-1 select-none text-gray-400"
                        >
                          ...
                        </li>
                      ) : (
                        <li key={pg}>
                          <button
                            type="button"
                            className={`px-2 py-1 rounded-md ${
                              pagination.currentPage === pg
                                ? "bg-[#204296] text-white"
                                : "text-gray-800"
                            }`}
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                currentPage: Number(pg),
                              }))
                            }
                          >
                            {pg}
                          </button>
                        </li>
                      )
                    )}
                    <li>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded-full ${
                          pagination.currentPage === pagination.totalPages
                            ? "text-gray-400 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={
                          pagination.currentPage === pagination.totalPages
                        }
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: prev.currentPage + 1,
                          }))
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
